import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import Layout from "../components/Layout";
import EditableText from "../components/EditableText";
import CEReportingPeriod from "../components/CEReportingPeriod";
import { 
  Plus, Award, Upload, FileText, Calendar, Building2, 
  CheckCircle, Clock, Edit2, Trash2, Download
} from "lucide-react";

const CETrackerPage = () => {
  const { user } = useAuth();
  const [ceRecords, setCeRecords] = useState([]);
  const [totalCredits, setTotalCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchCERecords();
  }, []);

  const fetchCERecords = async () => {
    try {
      const response = await axios.get(`${API}/ce`);
      setCeRecords(response.data.records);
      setTotalCredits(response.data.total_credits);
    } catch (error) {
      toast.error("Failed to load CE records");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file (JPG, PNG, etc.)");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/ce/upload-certificate`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success("Certificate uploaded and analyzed!");
      setShowUpload(false);
      // Show edit dialog with extracted data
      setEditingRecord(response.data);
      fetchCERecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload certificate");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleUpdateRecord = async (e) => {
    e.preventDefault();
    if (!editingRecord) return;

    try {
      await axios.put(`${API}/ce/${editingRecord.ce_id}`, {
        course_name: editingRecord.course_name,
        provider: editingRecord.provider,
        credits: parseFloat(editingRecord.credits) || 0,
        completion_date: editingRecord.completion_date,
        status: "confirmed"
      });
      toast.success("CE record updated!");
      setEditingRecord(null);
      fetchCERecords();
    } catch (error) {
      toast.error("Failed to update record");
    }
  };

  const handleDeleteRecord = async (ceId) => {
    if (!window.confirm("Are you sure you want to delete this CE record?")) return;

    try {
      await axios.delete(`${API}/ce/${ceId}`);
      toast.success("CE record deleted");
      fetchCERecords();
    } catch (error) {
      toast.error("Failed to delete record");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Unknown";
    try {
      return new Date(dateStr).toLocaleDateString("en-CA", {
        year: "numeric",
        month: "short",
        day: "numeric"
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="ce-tracker-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <EditableText
              blockKey="ce_tracker.title"
              fallback="CE Tracker"
              as="h1"
              className="text-2xl sm:text-3xl font-medium text-[#6B4E8A]"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            />
            <EditableText
              blockKey="ce_tracker.subtitle"
              fallback="Track your continuing education credits"
              as="p"
              className="text-[#64748B] mt-1"
              multiline
            />
          </div>
          <Dialog open={showUpload} onOpenChange={setShowUpload}>
            <DialogTrigger asChild>
              <Button className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full" data-testid="upload-certificate-btn">
                <Upload className="w-4 h-4 mr-2" />
                Upload Certificate
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="text-xl font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Upload CE Certificate
                </DialogTitle>
              </DialogHeader>
              <div className="mt-4 space-y-4">
                <div className="bg-[#FAF7F2] rounded-xl p-6 text-center border-2 border-dashed border-[#E5DFEC]">
                  <Upload className="w-12 h-12 mx-auto mb-4 text-[#8DB5A0]" />
                  <p className="text-[#6B4E8A] font-medium mb-2">
                    Upload your CE certificate image
                  </p>
                  <p className="text-sm text-[#64748B] mb-4">
                    AI will automatically extract course name, credits, date, and provider
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="certificate-upload"
                    data-testid="certificate-file-input"
                  />
                  <label htmlFor="certificate-upload">
                    <Button
                      type="button"
                      className="bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full cursor-pointer"
                      disabled={uploading}
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="select-file-btn"
                    >
                      {uploading ? "Analyzing..." : "Select Image"}
                    </Button>
                  </label>
                </div>
                <p className="text-xs text-[#64748B] text-center">
                  Supported formats: JPG, PNG, WEBP (max 10MB)
                </p>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Sprint 5 — Reporting Period setup + progress widget */}
        <CEReportingPeriod onChanged={fetchCERecords} />

        {/* Stats Card */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-[#6B4E8A] to-[#553D6F] rounded-2xl p-6 md:p-8 mb-8 text-white"
        >
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 rounded-2xl bg-[#8DB5A0] flex items-center justify-center">
              <Award className="w-8 h-8 text-[#6B4E8A]" />
            </div>
            <div>
              <p className="text-white/70 text-sm">Total Confirmed Credits</p>
              <p className="text-4xl font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {totalCredits.toFixed(1)}
              </p>
              <p className="text-white/70 text-sm mt-1">
                {ceRecords.filter(r => r.status === "confirmed").length} verified records
              </p>
            </div>
          </div>
        </motion.div>

        {/* Edit Dialog */}
        <Dialog open={!!editingRecord} onOpenChange={() => setEditingRecord(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Review CE Record
              </DialogTitle>
            </DialogHeader>
            {editingRecord && (
              <form onSubmit={handleUpdateRecord} className="mt-4 space-y-4">
                <p className="text-sm text-[#64748B] bg-[#FAF7F2] p-3 rounded-lg">
                  AI has extracted the following information. Please review and correct if needed.
                </p>
                <div>
                  <Label htmlFor="course_name">Course Name</Label>
                  <Input
                    id="course_name"
                    value={editingRecord.course_name}
                    onChange={(e) => setEditingRecord({ ...editingRecord, course_name: e.target.value })}
                    className="mt-2 bg-white border-[#E5DFEC] focus:border-[#6B4E8A]"
                    data-testid="edit-course-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="provider">Provider/Organization</Label>
                  <Input
                    id="provider"
                    value={editingRecord.provider}
                    onChange={(e) => setEditingRecord({ ...editingRecord, provider: e.target.value })}
                    className="mt-2 bg-white border-[#E5DFEC] focus:border-[#6B4E8A]"
                    data-testid="edit-provider-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="credits">CE Credits</Label>
                    <Input
                      id="credits"
                      type="number"
                      step="0.5"
                      min="0"
                      value={editingRecord.credits}
                      onChange={(e) => setEditingRecord({ ...editingRecord, credits: e.target.value })}
                      className="mt-2 bg-white border-[#E5DFEC] focus:border-[#6B4E8A]"
                      data-testid="edit-credits-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="completion_date">Completion Date</Label>
                    <Input
                      id="completion_date"
                      type="date"
                      value={editingRecord.completion_date}
                      onChange={(e) => setEditingRecord({ ...editingRecord, completion_date: e.target.value })}
                      className="mt-2 bg-white border-[#E5DFEC] focus:border-[#6B4E8A]"
                      data-testid="edit-date-input"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setEditingRecord(null)} className="rounded-full">
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-[#10B981] text-white hover:bg-[#059669] rounded-full" data-testid="confirm-ce-btn">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Confirm Record
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* CE Records List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white border border-[#E5DFEC] rounded-2xl p-6 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#F0ECF7] rounded-xl"></div>
                  <div className="flex-1">
                    <div className="h-5 bg-[#F0ECF7] rounded w-1/2 mb-2"></div>
                    <div className="h-4 bg-[#F0ECF7] rounded w-1/3"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : ceRecords.length > 0 ? (
          <div className="space-y-4">
            {ceRecords.map((record, idx) => (
              <motion.div
                key={record.ce_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-white border border-[#E5DFEC] rounded-2xl p-6"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#F0ECF7] flex items-center justify-center shrink-0">
                    <FileText className="w-6 h-6 text-[#8DB5A0]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {record.course_name || "Untitled Course"}
                        </h3>
                        <p className="text-sm text-[#64748B]">{record.provider || "Unknown Provider"}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          record.status === "confirmed" 
                            ? "bg-[#10B981]/10 text-[#10B981]" 
                            : "bg-[#8DB5A0]/10 text-[#8DB5A0]"
                        }`}>
                          {record.status === "confirmed" ? (
                            <span className="flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" /> Confirmed
                            </span>
                          ) : (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" /> Review
                            </span>
                          )}
                        </span>
                        <span className="px-3 py-1 bg-[#6B4E8A] text-white rounded-full text-sm font-medium">
                          {record.credits || 0} credits
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-sm text-[#64748B]">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(record.completion_date)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingRecord(record)}
                      className="rounded-full"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteRecord(record.ce_id)}
                      className="rounded-full text-[#EF4444] hover:bg-[#EF4444]/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white border border-[#E5DFEC] rounded-2xl">
            <Award className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
            <h3 className="text-lg font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              No CE records yet
            </h3>
            <p className="text-[#64748B] mb-4">
              Upload your first CE certificate to get started
            </p>
            <Button onClick={() => setShowUpload(true)} className="bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full">
              <Upload className="w-4 h-4 mr-2" />
              Upload Certificate
            </Button>
          </div>
        )}

        {/* Tips Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8 bg-[#FAF7F2] border border-[#E5DFEC] rounded-2xl p-6"
        >
          <h3 className="font-medium text-[#6B4E8A] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Tips for License Renewal
          </h3>
          <ul className="space-y-2 text-sm text-[#64748B]">
            <li>• Keep all your CE certificates organized in one place</li>
            <li>• Review your provincial requirements for the renewal cycle</li>
            <li>• Submit your CE records to your provincial registry before the deadline</li>
            <li>• Most provinces require a certain number of hours per renewal period</li>
          </ul>
        </motion.div>
      </div>
    </Layout>
  );
};

export default CETrackerPage;
